// =============================================================================
// volumesExcel.js
// Excel template export / import for Budget Setup volume uploads:
//   * Sales / sell-out volumes  (bud_sales_volumes)
//   * Production volumes        (bud_production_volumes)
//
// Layout is WIDE — one row per dimension combination, with twelve period
// columns (P1..P12) plus a Unit column. This matches the on-screen grid, so a
// user can export current data, edit in Excel, and re-import.
//
// Dimension columns carry in-cell dropdowns (data validation) backed by a
// hidden "Lists" sheet of master data — the same choices offered in the grid.
//
// Import expands each row into per-period entries
// ({ ...dims, financial_period, quantity, unit_of_measure }) and validates
// against the supplied reference data, returning { rows, errors } ready for
// volumeService.upsertSalesVolumes / upsertProductionVolumes.
//
// `ref` is the volume reference payload:
//   { customers, customerGroups, sites, stockCodes }
// =============================================================================
import ExcelJS from 'exceljs';

const DELIM = ' — ';         // separates "CODE — Description" in dropdown values
const NONE = '(None)';       // explicit "no value" choice for optional channels
const BLANK_ROWS = 200;      // empty rows (with dropdowns) appended after the data
const PERIODS = 12;

// "PROD-1 — Widget" -> "PROD-1"; plain "PROD-1" -> "PROD-1".
const codeOf = (raw) => {
  if (raw == null) return '';
  const s = String(raw).trim();
  const i = s.indexOf(DELIM);
  return (i >= 0 ? s.slice(0, i) : s).trim();
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const safeName = (s) => String(s || 'version').replace(/[^a-z0-9_-]+/gi, '_');

// ExcelJS cell.value may be a string, number, or rich-text/formula object.
const cellText = (cell) => {
  const v = cell?.value;
  if (v == null) return '';
  if (typeof v === 'object') return String(v.text ?? v.result ?? '');
  return String(v);
};

// Describe each upload kind: which dimension columns it has, which are
// required, and (for sales) which count as a "channel".
export function makeVolumeSpec(kind) {
  if (kind === 'production') {
    return {
      kind: 'production',
      sheet: 'Production Volumes',
      fileLabel: 'Production_Volumes',
      dims: [
        { key: 'site_code', header: 'Site', listKey: 'sites', codeKey: 'site_code', descKey: 'site_name', required: true },
        { key: 'stock_code', header: 'SKU', listKey: 'stockCodes', codeKey: 'stock_code', descKey: 'description', required: true },
      ],
      channelKeys: [],
      requireChannel: false,
    };
  }
  return {
    kind: 'sales',
    sheet: 'Sales Volumes',
    fileLabel: 'Sales_Volumes',
    dims: [
      { key: 'stock_code', header: 'SKU', listKey: 'stockCodes', codeKey: 'stock_code', descKey: 'description', required: true },
      { key: 'site_code', header: 'Site (sell-out)', listKey: 'sites', codeKey: 'site_code', descKey: 'site_name' },
      { key: 'customer_code', header: 'Customer', listKey: 'customers', codeKey: 'customer_code', descKey: 'customer_name' },
      { key: 'customer_group_code', header: 'Customer Group', listKey: 'customerGroups', codeKey: 'customer_group_code', descKey: 'group_name' },
    ],
    channelKeys: ['site_code', 'customer_code', 'customer_group_code'],
    requireChannel: true,
  };
}

const optionLabel = (r, dim) => `${r[dim.codeKey]}${DELIM}${r[dim.descKey] ?? ''}`;
const labelMapFor = (ref, dim) =>
  Object.fromEntries((ref[dim.listKey] || []).map((r) => [r[dim.codeKey], optionLabel(r, dim)]));

// ---- Export -----------------------------------------------------------------
// `rows` are grid rows straight from the view (dimension codes + period_data
// jsonb { "1": { quantity }, ... } + unit_of_measure). Pass [] for a blank
// template.
export async function exportVolumeTemplate({ kind, versionLabel, ref, rows = [], blankRows = BLANK_ROWS }) {
  const spec = makeVolumeSpec(kind);
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Mantle';
  wb.created = new Date();

  const ws = wb.addWorksheet(spec.sheet, { views: [{ state: 'frozen', ySplit: 1 }] });
  const lists = wb.addWorksheet('Lists');
  lists.state = 'veryHidden';

  // One option column per dimension on the hidden sheet (data from row 2).
  // Optional dims get a leading "(None)" choice.
  const listCols = 'ABCDEFGH';
  const dimListCol = {};
  const dimOptCount = {};
  spec.dims.forEach((dim, di) => {
    const col = listCols[di];
    dimListCol[dim.key] = col;
    const opts = (ref[dim.listKey] || []).map((r) => optionLabel(r, dim));
    const full = dim.required ? opts : [NONE, ...opts];
    dimOptCount[dim.key] = full.length;
    lists.getCell(`${col}1`).value = dim.header;
    full.forEach((v, i) => { lists.getCell(`${col}${i + 2}`).value = v; });
  });

  // Entry-sheet columns: dimensions, P1..P12, Unit.
  ws.columns = [
    ...spec.dims.map((d) => ({ header: d.header, key: d.key, width: 34 })),
    ...Array.from({ length: PERIODS }, (_, i) => ({ header: `P${i + 1}`, key: `p${i + 1}`, width: 9 })),
    { header: 'Unit', key: 'uom', width: 10 },
  ];
  const headerRow = ws.getRow(1);
  headerRow.font = { bold: true, color: { argb: 'FFF8FAFC' } };
  headerRow.alignment = { vertical: 'middle' };
  headerRow.height = 20;
  headerRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF000000' } };
  });

  // Pre-fill supplied rows (wide) so the file doubles as an edit template.
  const dimLabels = Object.fromEntries(spec.dims.map((d) => [d.key, labelMapFor(ref, d)]));
  rows.forEach((r, i) => {
    const row = ws.getRow(i + 2);
    spec.dims.forEach((d, di) => {
      const code = r[d.key];
      row.getCell(di + 1).value = code ? (dimLabels[d.key][code] || code) : (d.required ? '' : NONE);
    });
    const pd = r.period_data || {};
    for (let p = 1; p <= PERIODS; p++) {
      const cell = pd[String(p)];
      const q = cell && cell.quantity != null ? Number(cell.quantity) : null;
      if (q != null && !Number.isNaN(q)) row.getCell(spec.dims.length + p).value = q;
    }
    row.getCell(spec.dims.length + PERIODS + 1).value = r.unit_of_measure || '';
  });

  // Attach dropdowns to every dimension cell across existing + blank rows.
  const validation = (col, len) => ({
    type: 'list',
    allowBlank: true,
    formulae: [`=Lists!$${col}$2:$${col}$${len + 1}`],
    showErrorMessage: true,
    errorStyle: 'warning',
    error: 'Pick a value from the list.',
    errorTitle: 'Pick from the list',
  });
  const lastRow = rows.length + blankRows + 1;
  for (let r = 2; r <= lastRow; r++) {
    spec.dims.forEach((d, di) => {
      ws.getCell(`${listCols[di]}${r}`).dataValidation =
        validation(dimListCol[d.key], dimOptCount[d.key]);
    });
  }

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  triggerDownload(blob, `${spec.fileLabel}_Template_${safeName(versionLabel)}.xlsx`);
}

// ---- Import -----------------------------------------------------------------
// Returns { rows, errors }. `rows` is empty when there are any errors so the
// caller can refuse a partial import. Each returned row is a per-period entry.
export async function parseVolumeTemplate(file, kind, ref) {
  const spec = makeVolumeSpec(kind);
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const ws = wb.getWorksheet(spec.sheet) || wb.worksheets[0];
  if (!ws) return { rows: [], errors: ['Could not find a worksheet in the file.'] };

  const dimSets = Object.fromEntries(
    spec.dims.map((d) => [d.key, new Set((ref[d.listKey] || []).map((r) => r[d.codeKey]))]),
  );

  const out = [];
  const errors = [];
  const nDims = spec.dims.length;

  ws.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return; // header

    const dimRaw = spec.dims.map((_, di) => cellText(row.getCell(di + 1)));
    const periodRaw = Array.from({ length: PERIODS }, (_, p) => cellText(row.getCell(nDims + p + 1)));
    const uom = cellText(row.getCell(nDims + PERIODS + 1)).trim() || null;

    // Skip entirely blank rows.
    if (![...dimRaw, ...periodRaw].some((s) => String(s).trim())) return;

    const rowErrs = [];
    const dims = {};
    spec.dims.forEach((d, di) => {
      const raw = dimRaw[di].trim();
      if (!raw || raw === NONE) {
        if (d.required) rowErrs.push(`missing ${d.header}`);
        dims[d.key] = null;
        return;
      }
      const code = codeOf(raw);
      if (!dimSets[d.key].has(code)) rowErrs.push(`unknown ${d.header} "${code}"`);
      else dims[d.key] = code;
    });

    if (spec.requireChannel && !spec.channelKeys.some((k) => dims[k])) {
      rowErrs.push('specify at least one of site, customer, or customer group');
    }

    // Parse the 12 period cells; only non-blank cells become entries.
    const periodEntries = [];
    periodRaw.forEach((raw, p) => {
      const t = String(raw).trim();
      if (!t) return;
      const q = Number(t);
      if (Number.isNaN(q)) rowErrs.push(`P${p + 1} is not a number`);
      else periodEntries.push({ financial_period: p + 1, quantity: q });
    });

    if (rowErrs.length) {
      errors.push(`Row ${rowNumber}: ${rowErrs.join(', ')}`);
      return;
    }
    // A dimension row with no period values is simply ignored.
    periodEntries.forEach((pe) => out.push({ ...dims, financial_period: pe.financial_period, quantity: pe.quantity, unit_of_measure: uom }));
  });

  if (errors.length) return { rows: [], errors };
  return { rows: out, errors: [] };
}

export default { makeVolumeSpec, exportVolumeTemplate, parseVolumeTemplate };
